import http, { BASE_URL_3, HttpSuccessOrErrorType } from '../http';
import type {
    MenuGroupShareMenuBody,
    MenuStoreActivateBody,
    MenuStoreActivateResult,
    MenuStoreApplyToRestaurantBody,
    MenuStoreCreateMenuBody,
    MenuStorePatchFromCloudLibraryBody,
    MenuStorePatchFromCloudLibraryResult,
    MenuStorePatchFromRestaurantBody,
    MenuStorePublishFromCloudLibraryBody,
    MenuStorePublishFromCloudLibraryResult,
    MenuStorePublishFromRestaurantBody,
    MenuStoreListQueryParams,
    MenuStoreListResponse,
    MenuStoreMenuListItem,
    MenuStorePatchDishVisibilityBody,
    MenuStorePatchMenuBody,
    MenuStorePatchMenuResult,
    MenuStorePatchMenuStatusBody,
    MenuStorePatchMenuStatusResult,
    MenuStorePostLicenseCodesBody,
} from '../../models/menu-store.entity';

/**
 * 菜单商店 HTTP API（Shark `/v3/menu-store/*`，Bearer，无门店 string_id 路径前缀）
 */
const menuStoreApi = {
    /**
     * 商店列表：仅看已上架传 `status: 'published'`
     */
    async getMenuStoreMenus(params: MenuStoreListQueryParams): Promise<HttpSuccessOrErrorType<MenuStoreListResponse>> {
        return await http.get(`${BASE_URL_3}/menu-store/menus`, { params });
    },

    /**
     * 菜单详情（Apifox：`GET /v3/menu-store/menu/{id}`）
     * @param id 菜单 Mongo `_id` 字符串
     */
    async getMenuStoreMenuById(id: string): Promise<HttpSuccessOrErrorType<MenuStoreMenuListItem>> {
        return await http.get(`${BASE_URL_3}/menu-store/menu/${encodeURIComponent(id)}`);
    },

    /**
     * 我的菜单 · 我上传侧（分页参数与商店列表一致）
     */
    async getMenuStoreMyMenusUploaded(
        params: MenuStoreListQueryParams
    ): Promise<HttpSuccessOrErrorType<MenuStoreListResponse>> {
        return await http.get(`${BASE_URL_3}/menu-store/my-menus/uploaded`, { params });
    },

    /**
     * 我的菜单 · 已购买/已激活侧（分页参数与商店列表一致）
     */
    async getMenuStoreMyMenusPurchased(
        params: MenuStoreListQueryParams
    ): Promise<HttpSuccessOrErrorType<MenuStoreListResponse>> {
        return await http.get(`${BASE_URL_3}/menu-store/my-menus/purchased`, { params });
    },

    /**
     * 制作/发布菜单（JSON 中不传封面字段时省略 key）
     * @deprecated 菜单库同步商店请用 `postMenuStoreMenuFromRestaurant`
     */
    async postMenuStoreMenu(body: MenuStoreCreateMenuBody): Promise<HttpSuccessOrErrorType<unknown>> {
        return await http.post(`${BASE_URL_3}/menu-store/menu`, body);
    },

    /**
     * 从门店一级菜单发布至菜单商店（Apifox：`POST /v3/menu-store/menu/from-restaurant`）
     */
    async postMenuStoreMenuFromRestaurant(
        body: MenuStorePublishFromRestaurantBody
    ): Promise<HttpSuccessOrErrorType<unknown>> {
        return await http.post(`${BASE_URL_3}/menu-store/menu/from-restaurant`, body);
    },

    /**
     * 从云端菜品库菜单发布至菜单商店（OAS：`POST /v3/menu-store/menu/from-cloud-library`）。
     */
    async postMenuStoreMenuFromCloudLibrary(
        body: MenuStorePublishFromCloudLibraryBody
    ): Promise<HttpSuccessOrErrorType<MenuStorePublishFromCloudLibraryResult>> {
        return await http.post(`${BASE_URL_3}/menu-store/menu/from-cloud-library`, body);
    },

    /**
     * 将云端菜单同步至已有商店菜单（OAS：`PATCH /v3/menu-store/menu/{id}/from-cloud-library`）。
     * @param id 商店菜单 Mongo `_id`
     */
    async patchMenuStoreMenuFromCloudLibrary(
        id: string,
        body: MenuStorePatchFromCloudLibraryBody
    ): Promise<HttpSuccessOrErrorType<MenuStorePatchFromCloudLibraryResult>> {
        return await http.patch(
            `${BASE_URL_3}/menu-store/menu/${encodeURIComponent(id)}/from-cloud-library`,
            body
        );
    },

    /**
     * 更新菜单（下架等）
     * @param id Mongo `_id` 字符串
     */
    async patchMenuStoreMenu(id: string, body: MenuStorePatchMenuBody): Promise<HttpSuccessOrErrorType<MenuStorePatchMenuResult>> {
        return await http.patch(`${BASE_URL_3}/menu-store/menu/${encodeURIComponent(id)}`, body);
    },

    /**
     * 将门店侧一级菜单同步至已有商店菜单（Apifox：`PATCH /v3/menu-store/menu/{id}/from-restaurant`）
     * @param id 商店菜单 Mongo `_id`
     */
    async patchMenuStoreMenuFromRestaurant(
        id: string,
        body: MenuStorePatchFromRestaurantBody
    ): Promise<HttpSuccessOrErrorType<MenuStorePatchMenuResult>> {
        return await http.patch(`${BASE_URL_3}/menu-store/menu/${encodeURIComponent(id)}/from-restaurant`, body);
    },

    /**
     * 菜单上架/下架（Apifox：`PATCH /v3/menu-store/menu/{id}/status`，`delisted` = 软删）
     */
    async patchMenuStoreMenuStatus(
        id: string,
        body: MenuStorePatchMenuStatusBody
    ): Promise<HttpSuccessOrErrorType<MenuStorePatchMenuStatusResult>> {
        return await http.patch(`${BASE_URL_3}/menu-store/menu/${encodeURIComponent(id)}/status`, body);
    },

    /**
     * 批量更新菜品详情可见性（`PATCH /v3/menu-store/menu/{id}/dish-visibility`，body：`{ items: [...] }`）
     */
    async patchMenuStoreDishVisibility(
        menuId: string,
        body: MenuStorePatchDishVisibilityBody
    ): Promise<HttpSuccessOrErrorType<unknown>> {
        return await http.patch(`${BASE_URL_3}/menu-store/menu/${encodeURIComponent(menuId)}/dish-visibility`, body);
    },

    /**
     * 激活（授权码）；与「立刻使用」`postMenuStoreApplyToRestaurant` 为不同接口
     */
    async postMenuStoreActivate(body: MenuStoreActivateBody): Promise<HttpSuccessOrErrorType<MenuStoreActivateResult>> {
        return await http.post(`${BASE_URL_3}/menu-store/activate`, body);
    },

    /**
     * 查询指定菜单已生成的授权码（Apifox：`GET /v3/menu-store/menu/{id}/license-codes`）
     * @param menuId 菜单商店文档 Mongo `_id`
     */
    async getMenuStoreLicenseCodes(menuId: string): Promise<HttpSuccessOrErrorType<unknown>> {
        return await http.get(`${BASE_URL_3}/menu-store/menu/${encodeURIComponent(menuId)}/license-codes`);
    },

    /**
     * 为指定菜单生成授权码（Apifox：`POST /v3/menu-store/menu/{id}/license-codes`，body 可选 `count`，默认 1）
     * @param menuId 菜单商店文档 Mongo `_id`
     * @param body 生成条数；OAS 要求有 requestBody 时传 `{}` 即可走默认
     */
    async postMenuStoreLicenseCodes(
        menuId: string,
        body: MenuStorePostLicenseCodesBody = {}
    ): Promise<HttpSuccessOrErrorType<unknown>> {
        return await http.post(`${BASE_URL_3}/menu-store/menu/${encodeURIComponent(menuId)}/license-codes`, body);
    },

    /**
     * 已激活菜单「立刻使用」：将商店菜单同步到所选门店（与 `activate` 不同接口）
     */
    async postMenuStoreApplyToRestaurant(
        body: MenuStoreApplyToRestaurantBody
    ): Promise<HttpSuccessOrErrorType<unknown>> {
        return await http.post(`${BASE_URL_3}/menu-store/apply-to-restaurant`, body);
    },

    /**
     * 集团内分享菜单至多家门店（菜单库「同步至所选门店」）
     */
    async postMenuShare(body: MenuGroupShareMenuBody): Promise<HttpSuccessOrErrorType<unknown>> {
        return await http.post(`${BASE_URL_3}/menu/share`, body);
    },
};

export default menuStoreApi;
